import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import '../../styles/contests/ListeningMaster.css';
import mixpanel from '../../utils/mixpanel'
// Static import instead of dynamic import for better performance
import { getListeningGroups, getGroupChallenges } from '../../api/contests';

const ListeningInstructions = () => {
  const navigate = useNavigate();
  const { groupId } = useParams();
  
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Load group details and its challenges
  useEffect(() => {
    const fetchGroupAndChallenges = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Get all groups and find the selected one (using static import)
        const groups = await getListeningGroups();
        const group = groups.find(g => g.id === parseInt(groupId) || g.id === groupId);
        
        if (!group) {
          throw new Error(`Group with ID ${groupId} not found`);
        }
        
        // Set the group state
        setSelectedGroup(group);
        
        // Then load the challenges for this group
        const fetchedChallenges = await getGroupChallenges(groupId);
        setChallenges(fetchedChallenges);
        
      } catch (error) {
        console.error(`Error loading group ${groupId}:`, error);
        setError("Error loading challenge group information");
      } finally {
        setLoading(false);
      }
    };
    
    if (groupId) {
      fetchGroupAndChallenges();
    }
  }, [groupId]);
  
  // Handle start challenge button
  const handleStartChallenge = () => {
    // If challenges are already loaded, start with the first one
    if (challenges.length > 0) {
      // Track in Mixpanel
      mixpanel.track('Listening Challenge Started', {
        challenge_id: challenges[0].id || challenges[0].challenge_id,
        challenge_index: 0,
        group_id: selectedGroup?.id,
        group_name: selectedGroup?.name
      });
      
      // Navigate to the first challenge
      navigate(`/contests/challenge/${groupId}/0`);
    } else {
      // If no challenges, go back to list
      navigate('/contests');
    }
  };
  
  // Handle back button click
  const handleBackToGroups = () => {
    navigate('/contests');
  };
  
  if (loading) {
    return (
      <div className="listening-master-loading">
        <div className="spinner-ripple">
          <div></div>
          <div></div>
        </div>
        <p>Loading challenges...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="listening-master-no-challenges">
        <h2>Error</h2>
        <p>{error}</p>
        <button 
          className="listening-master-play-button"
          onClick={handleBackToGroups}
        >
          Back to Groups
        </button>
      </div>
    );
  }

  return (
    <div className="listening-master-instructions-wrapper full-screen">
      <div className="contest-instructions">
        <div className="instructions-content">
          {/* Rules Container */}
          <div className="rules-container">
            <div className="rules-header">
              <h2 className="rules-title">{selectedGroup?.name || "Listening Challenge"}</h2>
            </div>
            
            <ul className="rules-list">
              {[
                {
                  title: "Watch the Video",
                  text: "Listen carefully to a short clip of a Japanese speaker."
                },
                {
                  title: "One Play Only",
                  text: "You can only play the video once per round, so listen attentively!"
                },
                {
                  title: "Type It",
                  text: "Type exactly what you heard in the input field."
                },
                {
                  title: "Complete All Rounds",
                  text: `Finish all ${challenges.length} challenges to complete this group.`
                },
                {
                  title: "Accuracy Matters",
                  text: "Listen closely and type exactly what you hear for best results."
                }
              ].map((rule, index) => (
                <li key={index} className="rule-item">
                  <div className="rule-content">
                    <h3 className="rule-title">{rule.title}</h3>
                    <p className="rule-text" data-title={rule.title}>{rule.text}</p>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          
          {/* Start Button */}
          <button 
            className="contest-start-btn"
            onClick={handleStartChallenge}
          >
            Start Challenge
          </button>
          
          {/* Back Button - more prominent for instructions page */}
          <button 
            className="back-to-groups-button"
            onClick={handleBackToGroups}
            style={{ 
              background: 'none', 
              border: 'none', 
              color: '#4F46E5', 
              cursor: 'pointer', 
              textDecoration: 'underline', 
              marginTop: '15px',
              fontSize: '16px',
              fontWeight: '500',
              padding: '5px 15px'
            }}
          >
            Back to Groups
          </button>
        </div>
      </div>
    </div>
  );
};

export default ListeningInstructions;